/**
 * v0.18.62 — 退卡 / 退费 的**展示**格式化助手 (设计稿 v4)
 *
 * ## ⛔ 本文件只做「格式化」, 不做任何业务判断
 *   · 退费公式的唯一实现在后端 `RefundCalculator::compute()` ⇒ 前端**不许**重算
 *   · 审批档的唯一判据在后端 `RefundCalculator::tierFor()`  ⇒ 前端**不许**判档
 *   · 档位文案 (`approval_label`) / 档位序号 (`tier_order`) / 整句提示 (`tier_hint`)
 *     **一律由后端下发** ⇒ 前端**不许**建 `tier → 文案` 映射表
 *   ⇒ 这里只把后端下发的**数字**变成「人能读的字符串」。
 *
 * ## 金额展示格式 (设计稿明文)
 *   千分位 + 两位小数, **整数不带小数**:
 *     540    → '540'
 *     1234.5 → '1,234.50'
 *     3600   → '3,600'
 *     0.5    → '0.50'
 *     2333.33→ '2,333.33'
 *
 * ⚠️ **展示 ≠ 提交**: 提交发**纯数字** (⛔ 不发 '1,234.50' 这种带千分位的字符串 ——
 *   后端 `is_numeric('1,234.50')` = false ⇒ 422「退款金额必须是数字」)。
 *   ⇒ 用 `parseAmountInput()` 把输入框的值还原成数字再发。
 */

/** 中文数字 (用于「第几档」的后端 tier_order → 中文序数词) */
const CN_DIGITS = '零一二三四五六七八九';

/**
 * 金额 → 展示字符串 (千分位 + 两位小数, 整数不带小数)。
 *
 * @param {number|string} value
 * @returns {string}
 */
export function formatAmount(value) {
  const n = Number(value);
  if (value === null || value === '' || !isFinite(n)) return '';

  // 出口只舍一次 (2 位小数), 与后端 `RefundCalculator::money()` 同一口径
  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  const isInt = Math.abs(rounded - Math.round(rounded)) < 1e-9;
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: 2
  });
}

/**
 * 输入框里的字符串 → **纯数字** (提交用)。
 *
 * · 去掉千分位逗号与空白 ⇒ '1,234.50' → 1234.5
 * · 空串 ⇒ null (表示「没填」)
 * · 非数字 ⇒ NaN (调用方**必须**自己拦下来, ⛔ 不许把 NaN 发给后端 ——
 *   `JSON.stringify(NaN)` = 'null' ⇒ 后端当成「没填」⇒ 静默按系统核算额提交!)
 *
 * @param {string} raw
 * @returns {number|null}
 */
export function parseAmountInput(raw) {
  const s = String(raw === null || raw === undefined ? '' : raw).trim();
  if (s === '') return null;

  // 只保留数字 / 负号 / 小数点 (顺手把用户误输入的千分位逗号去掉)
  const cleaned = s.replace(/[^0-9.\-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return NaN;
  const n = Number(cleaned);
  return isFinite(n) ? n : NaN;
}

/**
 * 差额 (绝对值) → 展示字符串。差额 chip 用它 (「多退 ¥60」/「少退 ¥140」)。
 */
export function formatDelta(value) {
  const n = Math.abs(Number(value));
  if (!isFinite(n)) return '';
  return formatAmount(n);
}

/**
 * 后端 `tier_order` (1 / 2 / 3) → CSS class 后缀 (`tier-1` / `tier-2` / `tier-3`)。
 *
 * ⚠️ ⛔ **这不是** `tier → tier-N` 映射表 —— 档位**判定**在后端
 *   (`RefundCalculator::tierFor()`), 这里只是把后端给的**序号**直接当 class 后缀用。
 *
 * @param {number|string} tierOrder
 * @returns {string} 未知档位 ⇒ '' (前端不猜)
 */
export function tierClassName(tierOrder) {
  const n = Number(tierOrder);
  if (!isFinite(n) || n < 1 || n > 9) return '';
  return 'tier-' + Math.floor(n);
}

/**
 * 后端 `tier_order` (1 / 2 / 3) → 中文序数词 ('一档' / '二档' / '三档')。
 *
 * ⚠️ ⛔ **这不是** `tier → 文案` 映射表 —— 它把后端给的**数字**翻成中文数字,
 *   档位本身是后端判的 (`tierFor()`), 前端只是把序号显示出来。
 *
 * @param {number|string} tierOrder
 * @returns {string} 未知档位 ⇒ ''
 */
export function tierOrdinalText(tierOrder) {
  const n = Number(tierOrder);
  if (!isFinite(n) || n < 1 || n > 9) return '';
  return CN_DIGITS.charAt(Math.floor(n)) + '档';
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/refundFormat', { formatAmount: formatAmount,parseAmountInput: parseAmountInput,formatDelta: formatDelta,tierClassName: tierClassName,tierOrdinalText: tierOrdinalText, });